import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import CloseIcon from '@mui/icons-material/Close'
import HistoryIcon from '@mui/icons-material/History'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import { makeGetRequest } from 'utils/api'
import urls from 'utils/urls'
import WTypography from '../../../Components/whiteTypography'

/**
 * HistoriqueScoreModal (LAP-CP08)
 * -------------------------------
 * Modal « Historique » du Mon Conseiller Patrimonial : trace l'ÉVOLUTION du
 * score patrimonial dans le temps (courbe), avec une analyse de tendance
 * (delta depuis le 1er point, depuis le point précédent, verdict) et la liste
 * des snapshots passés (date, score, note).
 *
 * Source : GET /api/general/patrimonial-score/history/ (snapshots POSTés par le
 * conseiller à chaque génération). Le champ `dimension_scores` et `key_metrics`
 * sont disponibles dans chaque snapshot pour de futurs détails.
 */
function HistoriqueScoreModal({ open, onClose }) {
  const { t, i18n } = useTranslation('layout')
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const lang = (i18n.language || 'fr').split('-')[0]

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const fetchHistory = async () => {
      setLoading(true)
      setError(null)
      try {
        const { data } = await makeGetRequest(
          urls.PatrimonialScoreHistory,
          {},
          true,
        )
        const list = Array.isArray(data) ? data : data?.data || []
        if (!cancelled) setHistory(list)
      } catch (e) {
        if (!cancelled) setError(e?.message || 'error')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchHistory()
    return () => {
      cancelled = true
    }
  }, [open])

  // Les snapshots arrivent du plus récent au plus ancien : on inverse pour la
  // courbe chronologique (ancien → récent).
  const chrono = useMemo(
    () =>
      [...history]
        .filter((s) => s && s.global_score != null)
        .reverse()
        .map((s) => ({
          ...s,
          dateLabel: dayjs(s.created_at).locale(lang).format('DD MMM YY'),
          score: Number(s.global_score),
        })),
    [history, lang],
  )

  // Analyse d'évolution : delta total, delta récent, verdict de tendance.
  const analysis = useMemo(() => {
    if (chrono.length < 2) return null
    const first = chrono[0].score
    const last = chrono[chrono.length - 1].score
    const prev = chrono[chrono.length - 2].score
    const deltaTotal = last - first
    const deltaRecent = last - prev
    let trend = 'stable'
    if (deltaRecent > 1) trend = 'up'
    else if (deltaRecent < -1) trend = 'down'
    return { first, last, prev, deltaTotal, deltaRecent, trend, points: chrono.length }
  }, [chrono])

  const trendMeta = {
    up: { color: '#4ade80', Icon: TrendingUpIcon },
    down: { color: '#f87171', Icon: TrendingDownIcon },
    stable: { color: '#fbbf24', Icon: TrendingFlatIcon },
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0e1a36',
          color: 'white',
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.08)',
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Box display="flex" alignItems="center" gap={1.2}>
          <HistoryIcon sx={{ color: '#60a5fa' }} />
          <WTypography variant="h6" sx={{ fontWeight: 700 }}>
            {t('conseillerPatrimonial.historique.title')}
          </WTypography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#a0b4d6' }} aria-label={t('common.close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress sx={{ color: '#60a5fa' }} />
          </Box>
        ) : error ? (
          <WTypography variant="body2" sx={{ color: '#f87171 !important', py: 4 }}>
            {t('conseillerPatrimonial.historique.error')}
          </WTypography>
        ) : chrono.length === 0 ? (
          <Box py={5} textAlign="center">
            <WTypography variant="body1" sx={{ fontWeight: 600 }}>
              {t('conseillerPatrimonial.historique.emptyTitle')}
            </WTypography>
            <WTypography variant="body2" sx={{ color: '#a0b4d6 !important', mt: 1 }}>
              {t('conseillerPatrimonial.historique.emptyBody')}
            </WTypography>
          </Box>
        ) : (
          <>
            {/* Analyse d'évolution */}
            {analysis && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: 1.5,
                  mb: 3,
                }}
              >
                <StatBox
                  label={t('conseillerPatrimonial.historique.currentScore')}
                  value={`${analysis.last}/100`}
                  color="#dbe5f7"
                />
                <StatBox
                  label={t('conseillerPatrimonial.historique.sinceStart')}
                  value={`${analysis.deltaTotal >= 0 ? '+' : ''}${analysis.deltaTotal} pts`}
                  color={analysis.deltaTotal >= 0 ? '#4ade80' : '#f87171'}
                />
                <StatBox
                  label={t('conseillerPatrimonial.historique.lastChange')}
                  value={`${analysis.deltaRecent >= 0 ? '+' : ''}${analysis.deltaRecent} pts`}
                  color={trendMeta[analysis.trend].color}
                  Icon={trendMeta[analysis.trend].Icon}
                />
              </Box>
            )}

            {analysis && (
              <Box
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  bgcolor: `${trendMeta[analysis.trend].color}12`,
                  border: `1px solid ${trendMeta[analysis.trend].color}33`,
                }}
              >
                <WTypography variant="body2" sx={{ color: '#c1d0eb !important' }}>
                  {t(`conseillerPatrimonial.historique.trendBody.${analysis.trend}`, {
                    delta: Math.abs(analysis.deltaRecent),
                    total: Math.abs(analysis.deltaTotal),
                    points: analysis.points,
                  })}
                </WTypography>
              </Box>
            )}

            {/* Courbe d'évolution */}
            <Box sx={{ width: '100%', height: 260, mb: 3 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chrono} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fill: '#8da3c9', fontSize: 11 }}
                    stroke="rgba(255,255,255,0.15)"
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: '#8da3c9', fontSize: 11 }}
                    stroke="rgba(255,255,255,0.15)"
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#0b1530',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 8,
                      color: '#fff',
                    }}
                    labelStyle={{ color: '#a0b4d6' }}
                    formatter={(value) => [`${value}/100`, t('conseillerPatrimonial.historique.score')]}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#60a5fa"
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: '#60a5fa' }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>

            {/* Liste des snapshots (du plus récent au plus ancien) */}
            <WTypography
              variant="overline"
              sx={{ color: '#a0b4d6 !important', letterSpacing: 1.2 }}
            >
              {t('conseillerPatrimonial.historique.reportsTitle')}
            </WTypography>
            <Box display="flex" flexDirection="column" gap={1} mt={1}>
              {history
                .filter((s) => s && s.global_score != null)
                .map((s) => (
                  <Box
                    key={s.id}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <WTypography variant="body2" sx={{ color: '#dbe5f7 !important' }}>
                      {dayjs(s.created_at).locale(lang).format('DD MMMM YYYY · HH:mm')}
                    </WTypography>
                    <Box display="flex" alignItems="center" gap={1}>
                      {s.grade ? (
                        <Chip
                          label={s.grade}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(96,165,250,0.15)',
                            color: '#93c5fd',
                            fontWeight: 700,
                            height: 22,
                          }}
                        />
                      ) : null}
                      <WTypography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {s.global_score}/100
                      </WTypography>
                    </Box>
                  </Box>
                ))}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function StatBox({ label, value, color, Icon }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
        {label}
      </WTypography>
      <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
        {Icon ? <Icon sx={{ fontSize: 18, color }} /> : null}
        <WTypography variant="h6" sx={{ fontWeight: 700, color: `${color} !important` }}>
          {value}
        </WTypography>
      </Box>
    </Box>
  )
}

export default HistoriqueScoreModal
